import axios from 'axios';
import {
  APIError,
  RateLimitError,
  AuthenticationError,
  NotFoundError
} from '../utils/errors';

/**
 * Cloudflare API 客户端基类
 */
class CloudflareClient {
  constructor(config) {
    this.config = config;
    this.baseURL = '/api/cloudflare';

    this.zoneCache = new Map(); // 缓存 zone ID

    console.log('CloudflareClient init:', { baseURL: this.baseURL });

    // 创建 axios 实例
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      }
    });

    // 设置拦截器
    this.setupInterceptors();
  }

  /**
   * 获取认证头
   */
  getAuthHeaders() {
    if (this.config.apiToken) {
      return {
        'Authorization': `Bearer ${this.config.apiToken}`
      };
    } else if (this.config.apiKey && this.config.email) {
      return {
        'X-Auth-Key': this.config.apiKey,
        'X-Auth-Email': this.config.email
      };
    }
    throw new Error('No authentication credentials provided');
  }

  /**
   * 设置请求/响应拦截器
   */
  setupInterceptors() {
    // 请求拦截器
    this.client.interceptors.request.use(
      (config) => {
        console.debug(`API Request: ${config.method.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.client.interceptors.response.use(
      (response) => {
        console.debug(`API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      async (error) => {
        return this.handleError(error);
      }
    );
  }

  /**
   * 错误处理
   */
  async handleError(error) {
    if (!error.response) {
      // 网络错误
      console.error('API Network Error:', error);

      // 更详细的错误信息
      let errorMessage = 'Network error';

      if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Unable to connect to Cloudflare API. Please check your internet connection.';
      } else if (error.message.includes('CORS')) {
        errorMessage = 'CORS error. This might be a browser security issue.';
      } else {
        errorMessage = `Network error: ${error.message}`;
      }

      throw new APIError(errorMessage, 0);
    }

    const { status, data } = error.response;
    const message = data.errors?.[0]?.message || data.message || 'Unknown error';

    // 根据状态码处理
    switch (status) {
      case 401:
      case 403:
        throw new AuthenticationError(`Authentication failed: ${message}`);

      case 404:
        throw new NotFoundError('Resource', error.config.url);

      case 429:
        const retryAfter = error.response.headers['retry-after'] || 60;
        throw new RateLimitError(
          `Rate limit exceeded. Retry after ${retryAfter} seconds`,
          retryAfter
        );

      case 400:
        throw new APIError(`Bad request: ${message}`, status, data);

      case 500:
      case 502:
      case 503:
      case 504:
        throw new APIError(`Server error: ${message}`, status, data);

      default:
        throw new APIError(message, status, data);
    }
  }

  /**
   * 发起请求（带重试机制）
   */
  async request(method, path, data = null, options = {}) {
    const maxRetries = options.maxRetries || 3;
    const retryDelay = options.retryDelay || 1000;
    let lastError;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const config = {
          method,
          url: path,
          ...options
        };

        if (data) {
          if (method === 'GET') {
            config.params = data;
          } else {
            config.data = data;
          }
        }

        const response = await this.client.request(config);

        // Cloudflare API 响应格式验证
        if (!response.data.success) {
          const errors = response.data.errors || [];
          const errorMessage = errors.map(e => e.message).join(', ') || 'API request failed';
          throw new APIError(errorMessage, response.status, response.data);
        }

        return response.data;
      } catch (error) {
        lastError = error;

        // 如果是速率限制错误，等待后重试
        if (error instanceof RateLimitError) {
          console.warn(`Rate limited, waiting ${error.retryAfter} seconds...`);
          await this.sleep(error.retryAfter * 1000);
          continue;
        }

        // 如果是网络错误或服务器错误，重试
        if (
          error instanceof APIError &&
          (error.statusCode >= 500 || error.statusCode === 0) &&
          attempt < maxRetries - 1
        ) {
          console.warn(`Request failed, retrying... (${attempt + 1}/${maxRetries})`);
          await this.sleep(retryDelay * (attempt + 1));
          continue;
        }

        // 其他错误直接抛出
        throw error;
      }
    }

    throw lastError;
  }

  /**
   * GET 请求
   */
  async get(path, params = null, options = {}) {
    return this.request('GET', path, params, options);
  }

  /**
   * POST 请求
   */
  async post(path, data = null, options = {}) {
    return this.request('POST', path, data, options);
  }

  /**
   * PUT 请求
   */
  async put(path, data = null, options = {}) {
    return this.request('PUT', path, data, options);
  }

  /**
   * PATCH 请求
   */
  async patch(path, data = null, options = {}) {
    return this.request('PATCH', path, data, options);
  }

  /**
   * DELETE 请求
   */
  async delete(path, options = {}) {
    return this.request('DELETE', path, null, options);
  }

  /**
   * 根据域名获取 Zone ID
   */
  async getZoneId(domain) {
    // 检查缓存
    if (this.zoneCache.has(domain)) {
      return this.zoneCache.get(domain);
    }

    console.debug(`Fetching zone ID for domain: ${domain}`);

    try {
      const response = await this.get('/zones', { name: domain });

      if (!response.result || response.result.length === 0) {
        throw new NotFoundError('Zone', domain);
      }

      const zoneId = response.result[0].id;

      // 缓存结果
      this.zoneCache.set(domain, zoneId);

      console.debug(`Zone ID for ${domain}: ${zoneId}`);
      return zoneId;
    } catch (error) {
      throw new NotFoundError('Zone', domain);
    }
  }

  /**
   * 列出所有 Zones
   */
  async listZones(params = {}) {
    const response = await this.get('/zones', params);
    return response.result || [];
  }

  /**
   * 获取单个 Zone 信息
   */
  async getZone(zoneId) {
    const response = await this.get(`/zones/${zoneId}`);
    return response.result;
  }

  /**
   * 添加域名到 Cloudflare（创建 Zone）
   * @param {string} domain - 域名
   * @param {string|null} accountId - 账户 ID（可选）
   * @param {boolean} jumpStart - 是否自动导入 DNS 记录
   */
  async addZone(domain, accountId = null, jumpStart = false) {
    const data = {
      name: domain,
      jump_start: jumpStart
    };
    if (accountId) {
      data.account = { id: accountId };
    }
    const response = await this.post('/zones', data);
    return response.result;
  }

  /**
   * 获取指定账户下的 Worker 脚本列表
   * @param {string} accountId - 账户 ID
   */
  async listWorkerScripts(accountId) {
    const response = await this.get(`/accounts/${accountId}/workers/scripts`);
    return response.result || [];
  }

  /**
   * 获取指定账户下的 Pages 项目列表
   * @param {string} accountId - 账户 ID
   */
  async listPagesProjects(accountId) {
    const response = await this.get(`/accounts/${accountId}/pages/projects`);
    return response.result || [];
  }

  /**
   * 在指定 Zone 下创建 Worker Route
   * @param {string} zoneId - Zone ID
   * @param {string} pattern - 路由模式（如 example.com/*）
   * @param {string} scriptName - Worker 脚本名
   */
  async createWorkerRoute(zoneId, pattern, scriptName) {
    const response = await this.post(`/zones/${zoneId}/workers/routes`, {
      pattern,
      script: scriptName
    });
    return response.result;
  }

  /**
   * 为 Worker 绑定自定义域名（Custom Domain）
   * @param {string} accountId - 账户 ID
   * @param {string} serviceName - Worker 脚本名
   * @param {string} hostname - 需要绑定的域名/子域名
   * @param {string|null} zoneId - Zone ID（可选）
   */
  async attachWorkerDomain(accountId, serviceName, hostname, zoneId = null) {
    const data = {
      hostname,
      service: serviceName
    };

    if (zoneId) {
      data.zone_id = zoneId;
    }

    const response = await this.put(`/accounts/${accountId}/workers/domains`, data);
    return response.result;
  }

  /**
   * 列出 Worker 自定义域名
   * @param {string} accountId - 账户 ID
   * @param {object} params - 过滤参数（hostname/service/zone_id/zone_name）
   */
  async listWorkerDomains(accountId, params = {}) {
    const response = await this.get(`/accounts/${accountId}/workers/domains`, params);
    return response.result || [];
  }

  /**
   * 列出 Zone 下的 Worker Routes
   * @param {string} zoneId - Zone ID
   * @param {object} params - 过滤参数（pattern）
   */
  async listWorkerRoutes(zoneId, params = {}) {
    const response = await this.get(`/zones/${zoneId}/workers/routes`, params);
    return response.result || [];
  }

  /**
   * 为 Pages 项目绑定自定义域名
   * @param {string} accountId - 账户 ID
   * @param {string} projectName - Pages 项目名称
   * @param {string} domain - 自定义域名
   */
  async createPagesDomain(accountId, projectName, domain) {
    const response = await this.post(`/accounts/${accountId}/pages/projects/${projectName}/domains`, {
      name: domain
    });
    return response.result;
  }

  /**
   * 列出 Pages 项目的自定义域名
   * @param {string} accountId - 账户 ID
   * @param {string} projectName - Pages 项目名称
   */
  async listPagesDomains(accountId, projectName) {
    const response = await this.get(`/accounts/${accountId}/pages/projects/${projectName}/domains`);
    return response.result || [];
  }

  /**
   * 查询 Zone 下指定主机名的 DNS 记录
   * @param {string} zoneId - Zone ID
   * @param {string} hostname - 主机名
   */
  async listDNSRecordsByName(zoneId, hostname) {
    const response = await this.get(`/zones/${zoneId}/dns_records`, {
      name: hostname,
      per_page: 100
    });
    return response.result || [];
  }

  /**
   * 延迟函数
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 清除缓存
   */
  clearCache() {
    this.zoneCache.clear();
  }

  /**
   * 获取当前用户的账户列表
   */
  async listAccounts() {
    const response = await this.get('/accounts', { per_page: 50 });
    return response.result || [];
  }
}

export default CloudflareClient;
